<script lang="ts">
	import { ContextMenuButton } from "context-menu"
	import { DataHandlerMedia, type Media } from "luxedo-data"
	import { RenderProgressIndicator } from "../rendering"
	import { mediaProgressHandler } from "./MediaProgressHandler.svelte"
	import { Toast } from "toaster"
	import { TileLabel } from "../file-system"

	type EventHandler<T extends Event> = (
		e: T & {
			currentTarget: EventTarget
		},
	) => void

	interface Props {
		media: Media
		dragging?: boolean
		selected?: boolean
		onclick?: EventHandler<MouseEvent>
		ondblclick?: EventHandler<MouseEvent>
		oncontextmenu?: EventHandler<MouseEvent>
		ondragend?: EventHandler<DragEvent>
		ondragstart?: EventHandler<DragEvent>
		ontouchstart?: EventHandler<TouchEvent>
		ontouchend?: EventHandler<TouchEvent>
		ontouchcancel?: EventHandler<TouchEvent>
	}

	let {
		media,
		dragging,
		selected,
		onclick,
		ondblclick,
		oncontextmenu,
		ondragend,
		ondragstart,
		ontouchcancel,
		ontouchend,
		ontouchstart,
	}: Props = $props()

	let processingFailed = $derived(mediaProgressHandler.progress[media.id]?.status === "fail")
	let isProcessing = $derived(mediaProgressHandler.progress[media.id]?.status === "processing")
	let progress = $derived(mediaProgressHandler.progress[media.id]?.progress)

	async function onRename(newName: string) {
		try {
			await DataHandlerMedia.rename(media, newName)
			Toast.success("Media renamed.")
		} catch (e) {
			console.error("[ERROR] ", e)

			Toast.error("There was an error renaming this media, please refresh and try again.")
		}
	}
</script>

<div
	class="tile-container"
	class:dragging
	class:selected
	id="media-tile-{media.id}"
	role="button"
	title={media.name}
	tabindex="0"
	onkeydown={() => {}}
	{onclick}
	{ondblclick}
	{oncontextmenu}
	{ondragend}
	{ondragstart}
	{ontouchcancel}
	{ontouchend}
	{ontouchstart}
	draggable={true}
>
	{#if oncontextmenu}
		<div class="ctx-menu-btn">
			<ContextMenuButton onClick={oncontextmenu} />
		</div>
	{/if}
	<div class="tile-icon-container">
		<div class="tile">
			{#if isProcessing}
				<RenderProgressIndicator {progress} />
			{:else if processingFailed}
				<span class="failed-render"> ⚠ Processing failed </span>
			{:else}
				<img src={media.src.thumbnail} alt="{media.name} Thumbnail" />
			{/if}
		</div>
	</div>
	<div class="tile-label-container">
		<TileLabel file={media} {onRename} />
		<span class="media-type">
			{media.fileType}
		</span>
	</div>
</div>

<style>
	.tile-container.selected :global(.tile-label) {
		color: var(--color-main);
	}

	.tile-container .tile-icon-container {
		border-radius: var(--br);
		outline-color: var(--color-main);
		outline-style: solid;
		outline-width: 0px;

		transition: outline-width var(--transition-duration) var(--transition-timing);
		box-sizing: border-box;
	}

	.tile-container.selected .tile-icon-container {
		outline-width: 4px;
	}

	.tile {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.failed-render {
		line-height: 1;
		text-align: center;
		color: var(--ct-red);
	}
</style>
